/**
 * LanguageToggle — smart EN/ES switcher used in nav, footer, and elsewhere.
 *
 * Behavior:
 *  - Writes preference to cookie `wts_lang` (365d, path=/) + localStorage for redundancy.
 *  - Updates <html lang="..."> via i18n listener.
 *  - If the current URL is a native Spanish page (/es or /es/:slug), toggling to EN
 *    navigates back to `/` (English home). Otherwise toggling just flips i18n in place.
 *  - If on any English page and toggling to ES, stays on the same URL; the component
 *    tree re-renders with Spanish strings where translations are available.
 *
 * Accessibility: uses aria-label + native <button>; focus ring preserved.
 */
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { en2es, es2en } from '../utils/languageRoutes';

export default function LanguageToggle({ className = '', variant = 'nav' }) {
  const { t, i18n } = useTranslation(['nav', 'common']);
  const location = useLocation();
  const navigate = useNavigate();

  const currentLang = (i18n.language || 'en').split('-')[0];
  const isNativeSpanishRoute = location.pathname === '/es' || location.pathname.startsWith('/es/');
  const targetLang = currentLang === 'es' || isNativeSpanishRoute ? 'en' : 'es';

  const handleToggle = () => {
    // Persist preference early so navigation still reflects it.
    i18n.changeLanguage(targetLang);

    if (targetLang === 'en' && isNativeSpanishRoute) {
      // Navigate to the mapped English sibling route (falls back to '/').
      navigate(es2en(location.pathname));
      return;
    }
    if (targetLang === 'es' && !isNativeSpanishRoute) {
      // Try the EN→ES route map; if we have a dedicated Spanish page for this
      // URL, send the user there so they see fully-translated content + nav
      // instead of falling back to the in-place i18next pass (which leaves
      // hardcoded English on legacy landing pages).
      const esRoute = en2es(location.pathname);
      if (esRoute) {
        navigate(esRoute);
        return;
      }
    }
    // For all other cases we stay on the same URL; components re-render via
    // useTranslation() where translation keys are in place.
  };

  const label = targetLang === 'es' ? 'ES' : 'EN';
  const tip = t('common:languageToggle.switchTo', {
    lang: targetLang === 'es' ? t('common:languageToggle.spanish') : t('common:languageToggle.english'),
  });

  const base = variant === 'footer'
    ? 'inline-flex items-center gap-1.5 text-xs font-semibold text-slate-300 hover:text-white'
    : 'inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-blue-600 border border-slate-200 rounded-full px-2.5 py-1';

  return (
    <button
      type="button"
      onClick={handleToggle}
      className={`${base} ${className}`}
      aria-label={tip}
      title={tip}
      data-testid="language-toggle"
      data-lang-target={targetLang}
    >
      <Globe className="h-3.5 w-3.5" aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}
